'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

import { EASE, HAIRLINE } from '@/components/page-kit'

/* LMS's device: the margin rule. A single vertical hairline sits a fixed
   distance in from the left, ordinals hang in the margin to its left, and every
   line of content is indented off it — the ruled left margin of a syllabus or a
   course catalogue.

   It is the counterpart to the Accounts page's centre rule: same ruled-document
   family, opposite construction (one axis with content on both sides there, one
   margin with everything hanging off it here).

   The rule is one absolutely-positioned element rather than a per-row border,
   so it runs unbroken through the row rules it crosses. */

/** Margin width. The ordinal column and the rule position must share it. */
const MARGIN = 'grid-cols-[2.5rem_1fr] sm:grid-cols-[3.25rem_1fr]'
const RULE_X = 'left-10 sm:left-[3.25rem]'

export function MarginList({
  children,
  className,
  shown,
}: {
  children: React.ReactNode
  className?: string
  shown: boolean
}) {
  return (
    <div className={cn('relative border-t', HAIRLINE, className)}>
      <span
        aria-hidden
        className={cn(
          'absolute inset-y-0 w-px origin-top bg-border/70',
          RULE_X
        )}
        style={{
          transform: shown ? 'scaleY(1)' : 'scaleY(0)',
          transition: `transform 900ms ${EASE} 60ms`,
        }}
      />
      {children}
    </div>
  )
}

export function MarginRow({
  index,
  children,
  shown,
  className,
}: {
  index: number
  children: React.ReactNode
  shown: boolean
  className?: string
}) {
  return (
    <div
      className={cn('grid border-b', MARGIN, HAIRLINE, className)}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'none' : 'translateY(8px)',
        transition: `opacity 480ms ${EASE} ${index * 70}ms, transform 480ms ${EASE} ${index * 70}ms`,
      }}
    >
      {/* The ordinal hangs in the margin, set against the rule. */}
      <span className="pr-3 pt-[3px] text-right text-[11px] tabular-nums text-muted-foreground/60 sm:pr-4">
        {String(index + 1).padStart(2, '0')}
      </span>

      <div className="min-w-0 pl-5 sm:pl-7">{children}</div>
    </div>
  )
}
